import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import moment from 'moment'

import Table from '../../../Invoices/Table'

import styles from './styles.module.scss'

import { getCurrency } from '../../../../helpers/currencies'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')} ${currency}`
}

const RevenueByCategory = ({transactions}) => {
  const { t } = useTranslation()
    
  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const data = useMemo(() => {
    const invoices = transactions.filter(tr => tr.TransactionType === 'INVOICE')
    const categoryMap = invoices.reduce((acc, transaction) => {
      const categoryName = transaction['Product.TopParent'] || 'Uncategorized'
      return {
        ...acc,
        [categoryName]: acc[categoryName] ? [...acc[categoryName], transaction] : [transaction],
      }
    }, {})
    const totalAmount = invoices.reduce((acc, transaction) => acc + (transaction['Sum.Incoming'] || 0), 0)
    return Object.entries(categoryMap).map(([category, transactions]) => {
      const {amount, quantity} = transactions.reduce((acc, transaction) => {
        return {
          ...acc,
          amount: acc.amount + (transaction['Sum.Incoming'] || 0),
          quantity: acc.quantity + (transaction['Amount.In'] || 0),
        }
      }, {
        amount: 0,
        quantity: 0,
        unit: '',
      })
      
      return {
        name: category,
        amount: amount,
        quantity: quantity,
        unit: transactions[0]['Product.MeasureUnit'] || '',
        percentage: totalAmount > 0 ? (amount / totalAmount * 100) : 0,
      }
    })
  }, [transactions])

  const columns = useMemo(() => ([
    columnHelper.accessor('name', {
      id: 'name',
      header: t('Category'),
      enableSorting: false,
      cell: ({ getValue, row }) => {
        return `${getValue()}`
      }
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Borrowed'),
    }),
    columnHelper.accessor('quantity', {
      id: 'quantity',
      enableSorting: false,
      cell: ({ getValue }) => numeral(getValue()).format('0.[00]'),
      header: t('Quantity'),
    }),
    columnHelper.accessor('unit', {
      id: 'unit',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Unit'),
    }),
    columnHelper.accessor('percentage', {
      id: 'percentage',
      enableSorting: false,
      cell: ({ getValue }) => numeral(getValue()).format('0.[00]') + '%',
      header: t('Percentage %'),
    }),
  ]), [t, currency.symbol])

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('Revenue by category')}</div>
      </div>
      <Table
        table={table}
        columns={columns}
        wrapperStyle={{flex: 1}}
        headerClassName={styles.tableHeader} />
    </div>
  )
}

export default RevenueByCategory
